import { isEmpty, isNil } from 'lodash'

import {
  BOOLEAN_TYPE_KEY,
  getRowTypeKey,
  isLogicField,
  LOGIC_COMBINATOR,
  normalizeFieldKey,
  PICK_LIST_TYPE_KEY,
} from './index'
import { FIELD_GROUP } from './fieldGroups.helpers'
import {
  BOOLEAN_OPERAND_OPTIONS,
  EDGE_OPERATOR,
  getLinksForSource,
  getLinksForTarget,
  getMoveBlockedReason,
  getPendingOptionChoices,
  hasStagedOptions,
  operatorNeedsValue,
  parseOperandList,
} from './fieldLinks.helpers'

// The Branches view answers the question the field list can't: given one call,
// which fields does it actually produce? A flow with sixty edges hanging off one
// call-type field has no useful "all fields" reading — no call gets all of them —
// so the view asks for a value first and then shows the note that value produces.
//
// Everything here is pure over (promptFields, links, selection) so the branch
// arithmetic is testable without rendering.

// No value chosen for a condition field. Its dependents still list, marked as
// admitted-only-if — the one case where restating the condition is news.
export const ANY_VALUE = '__any__'

// The operators a branch can be picked from. A gate like "Refund Amount > 500"
// isn't a branch anyone can select, so its targets stay indeterminate and say so
// rather than the picker inventing a threshold to choose between.
const ENUMERABLE_OPERATORS = [EDGE_OPERATOR.eq, EDGE_OPERATOR.neq, EDGE_OPERATOR.in]

const PRESENCE_OPERATORS = [EDGE_OPERATOR.exists, EDGE_OPERATOR.not_exists]

// A presence gate names no value, so the branch it makes is whether the field
// answered at all. These stand for the two sides of that, since a field gated
// only on presence otherwise gives the picker nothing to offer.
export const HAS_VALUE = '__has_value__'
export const NO_VALUE = '__no_value__'

const PRESENCE_CHOICES = [
  { value: HAS_VALUE, label: 'Has a value' },
  { value: NO_VALUE, label: 'No value' },
]

const operandsOf = (link) => {
  if (!operatorNeedsValue(link.operator)) {
    return []
  }
  return link.operator === EDGE_OPERATOR.in
    ? parseOperandList(link.compare_value)
    : [link.compare_value]
}

// Whether `link` passes for a source sitting at `selectedValue`. null means the
// question can't be answered — no value picked, or an operator the picker can't
// offer a value for — which admits the target and flags it as conditional.
export const evaluateLink = (link, selectedValue) => {
  if (isNil(link.operator)) {
    // No operator is an unconditional edge: it feeds the source's answer into
    // the target's prompt, it doesn't gate anything.
    return true
  }
  if (selectedValue === ANY_VALUE || isNil(selectedValue)) {
    return null
  }
  // Any real value answers a presence gate; the sentinel for "no value" is the
  // only thing that fails one.
  if (link.operator === EDGE_OPERATOR.exists) {
    return selectedValue !== NO_VALUE
  }
  if (link.operator === EDGE_OPERATOR.not_exists) {
    return selectedValue === NO_VALUE
  }
  // "Has a value" says the field answered, not what it answered, so a gate on a
  // specific value can't be settled from it.
  if (selectedValue === HAS_VALUE) {
    return null
  }
  if (selectedValue === NO_VALUE) {
    // A field that answered nothing matches no value and differs from all of them.
    return link.operator === EDGE_OPERATOR.neq
  }
  const operands = operandsOf(link)
  switch (link.operator) {
    case EDGE_OPERATOR.eq:
      return operands[0] === selectedValue
    case EDGE_OPERATOR.neq:
      return operands[0] !== selectedValue
    case EDGE_OPERATOR.in:
      return operands.includes(selectedValue)
    default:
      // Numeric comparisons against a picked option name aren't meaningful.
      return null
  }
}

// A logic field's incoming edges are the terms it combines, not gates: it runs on
// every call and answers Yes or No from them. So a link into one never closes or
// counts as gating it — what such a link does gate is whatever the logic field
// itself gates, reached through it.
const getLogicKeys = (promptFields = []) =>
  promptFields.filter(isLogicField).map((field) => normalizeFieldKey(field.key))

// The fields a field's conditions decide, gates only. A field that gates one
// field and feeds another's prompt via a `$token` reports one, not two — the label
// says "condition field", and a data link is not a condition. A term into a logic
// field counts the fields that logic field gates, since that's what the term
// decides.
const collectTargets = (links, fieldKey, logicKeys, targets, visited) => {
  getLinksForSource(links, fieldKey)
    .filter((link) => !isNil(link.operator))
    .forEach((link) => {
      const target = normalizeFieldKey(link.target)
      if (!logicKeys.includes(target)) {
        targets.add(target)
      } else if (!visited.has(target)) {
        visited.add(target)
        collectTargets(links, target, logicKeys, targets, visited)
      }
    })
  return targets
}

const countTargets = (links, fieldKey, logicKeys = []) =>
  collectTargets(links, fieldKey, logicKeys, new Set(), new Set()).size

// The fields something is gated on, in the order they appear in the note. A
// field only becomes a picker row once the branch above it admits it, so this is
// the full set and resolveBranch decides which of them are reachable.
export const getBranchConditionFields = (promptFields = [], links = []) => {
  const logicKeys = getLogicKeys(promptFields)
  return (
    promptFields
      .filter((field) => !isEmpty((field.key ?? '').trim()))
      .map((field) => ({ field, gatesCount: countTargets(links, field.key, logicKeys) }))
      // A field that only feeds its answer into another's prompt gates nothing,
      // and nor does one that only feeds a logic field gating nothing — so there
      // is no branch to pick from either.
      .filter(({ gatesCount }) => gatesCount > 0)
  )
}

// Only offered where a presence gate actually reads them, so every choice in the
// picker stays a branch the flow really has.
const presenceChoicesFor = (field, links) =>
  getLinksForSource(links, field?.key).some((link) => PRESENCE_OPERATORS.includes(link.operator))
    ? PRESENCE_CHOICES
    : []

// What a condition field can be branched on. A selection field offers its whole
// option list — a call type that gates nothing is still a call worth previewing —
// and everything else offers the literals its own edges test against, so the
// choices are always branches that exist rather than values invented here.
export const getBranchChoices = (field, links, optionsByFieldName = {}, pendingTaxonomies = {}) => {
  const typeKey = getRowTypeKey(field)
  // Yes/No has both answers whatever the edges say. Deriving them from the gates
  // would offer only the side something hangs off, and "what does a No call
  // produce" is exactly the question worth asking of a branch that leads
  // nowhere. Shown by name, since 'true'/'false' is storage, not language.
  if (typeKey === BOOLEAN_TYPE_KEY) {
    return [...BOOLEAN_OPERAND_OPTIONS, ...presenceChoicesFor(field, links)]
  }
  const isSelection = typeKey === PICK_LIST_TYPE_KEY
  // An edit applied for testing is what the field's options are on screen; the
  // org field list only learns about it once the taxonomy is saved.
  const pendingChoices = isSelection ? getPendingOptionChoices(field, pendingTaxonomies) : null
  if (!isNil(pendingChoices)) {
    return [...pendingChoices, ...presenceChoicesFor(field, links)]
  }
  if (isSelection && hasStagedOptions(field)) {
    // Not minted yet, so a gate on it holds option names rather than uuids.
    return [
      ...field.new_taxonomy_options.map((option) => ({ value: option.name, label: option.name })),
      ...presenceChoicesFor(field, links),
    ]
  }
  if (isSelection) {
    const orgOptions = optionsByFieldName[normalizeFieldKey(field.key)] ?? []
    // The org list merges the field's options from every active playbook, so a
    // playbook with its own copy would otherwise offer another's options, and an
    // option deleted here would linger while another flow still has it. A flow
    // that isn't active yet has nothing of its own in the list; it keeps the
    // merged one rather than none.
    const ownOptions = orgOptions.filter((option) => option.taxonomy_uuid === field.taxonomy_uuid)
    const options = isEmpty(ownOptions) ? orgOptions : ownOptions
    if (!isEmpty(options)) {
      return [
        ...options.map((option) => ({ value: option.category_uuid, label: option.name })),
        ...presenceChoicesFor(field, links),
      ]
    }
  }

  const seen = new Set()
  const choices = []
  getLinksForSource(links, field.key)
    .filter((link) => ENUMERABLE_OPERATORS.includes(link.operator))
    .forEach((link) => {
      operandsOf(link).forEach((operand) => {
        if (!seen.has(operand)) {
          seen.add(operand)
          choices.push({ value: operand, label: operand })
        }
      })
    })
  return [...choices, ...presenceChoicesFor(field, links)]
}

// The org field list carries each selection field's options; index them by field
// name so a condition field's branch choices are its whole option list rather
// than only the values its edges happen to test.
export const buildOptionsByFieldName = (orgFieldNames = []) => {
  const byName = {}
  orgFieldNames.forEach((field) => {
    if (!isEmpty(field.options)) {
      byName[normalizeFieldKey(field.name)] = field.options
    }
  })
  return byName
}

// Reordering from a branch, where the list is a subset of the note. The order
// that matters is the full prompt_fields array, and the branch shows only the
// fields this call runs — so a drop reads as "put this field where that one is"
// against the whole array, not as an index into what happens to be visible.
// Fields the branch skipped keep their relative places around it.
//
// `beforeKey` is the visible field the moved one should land above, or null to
// go last. Agent-visible fields lead the flat array afterwards, the same
// invariant the field list keeps.
export const moveBranchField = (promptFields, movedKey, beforeKey, nextShownToAgent) => {
  const moved = promptFields.find(
    (field) => normalizeFieldKey(field.key) === normalizeFieldKey(movedKey)
  )
  if (isNil(moved)) {
    return promptFields
  }
  const updated = isNil(nextShownToAgent) ? moved : { ...moved, show_to_user: nextShownToAgent }
  const without = promptFields.filter((field) => field !== moved)
  const before = isNil(beforeKey)
    ? -1
    : without.findIndex((field) => normalizeFieldKey(field.key) === normalizeFieldKey(beforeKey))

  const next = [...without]
  next.splice(before === -1 ? without.length : before, 0, updated)
  return [
    ...next.filter((field) => field.show_to_user === true),
    ...next.filter((field) => field.show_to_user !== true),
  ]
}

// A drop, resolved against the whole flow. Kept out of the view because the
// index arithmetic is the part most likely to be subtly wrong: the indices are
// positions in a filtered, grouped list, and what they have to become is a
// position in prompt_fields.
//
// `rowsByGroup` maps each drop list's id to the rows it is showing. Returns
// either the reason the move is refused or the next prompt_fields.
export const planBranchDrop = ({ promptFields, links, rowsByGroup, source, destination }) => {
  const moved = rowsByGroup[source.droppableId]?.[source.index]?.field
  if (isNil(moved)) {
    return { promptFields }
  }

  const crossesGroups = source.droppableId !== destination.droppableId
  const intoAgentGroup = destination.droppableId === FIELD_GROUP.SHOWN_TO_AGENT
  if (crossesGroups) {
    // An agent-visible field can never wait on one processed later, so the rule
    // the field list enforces on a cross-group drag applies here too.
    const blockedReason = getMoveBlockedReason(promptFields, links, moved, intoAgentGroup)
    if (!isEmpty(blockedReason)) {
      return { blockedReason }
    }
  }

  // The drop index counts the destination list as it looks without the dragged
  // row in it, so the row it lands above is found in that same list.
  const destinationKeys = (rowsByGroup[destination.droppableId] ?? [])
    .filter((row) => row.field !== moved)
    .map((row) => row.field.key)

  return {
    promptFields: moveBranchField(
      promptFields,
      moved.key,
      destinationKeys[destination.index] ?? null,
      crossesGroups ? intoAgentGroup : null
    ),
  }
}

// Where a row came from, which is what the view shows instead of restating the
// condition the reader just chose.
export const PROVENANCE = {
  // Ungated: on every call, whatever the branch.
  always: 'always',
  // The deciding gate names exactly this value — nothing else produces it.
  only: 'only',
  // The deciding gate names several values, so other branches show it too.
  shared: 'shared',
  // Admitted only because a condition above it is still unchosen.
  conditional: 'conditional',
  // Gated, but on whether the source answered rather than on any value — so
  // there is no count of branches to give, only the condition itself.
  presence: 'presence',
}

// The narrowest gate on a row bounds how many branches can produce it, so that's
// the one worth describing. Fan-in is AND, so a field pinned to one value by any
// of its gates is exclusive to that value however wide its other gates are.
// A presence gate names no operand, but it is the WIDEST condition there is —
// any value satisfies it. Counting its zero operands as narrow made it beat
// every value gate on a fan-in row, which then read "shared · 0 Reasons" for a
// field that only one value produces.
const gateWidth = (link) =>
  PRESENCE_OPERATORS.includes(link.operator) ? Number.POSITIVE_INFINITY : operandsOf(link).length

const narrowestLink = (rowLinks) =>
  rowLinks
    // A data link admits nothing and excludes nothing — the target runs
    // whenever its source did. Left in, its null compare_value counts as one
    // operand and it wins as "narrowest", labelling an ungated row "only this
    // <source>".
    .filter((link) => !isNil(link.operator))
    .reduce((narrowest, link) => {
      if (isNil(narrowest)) {
        return link
      }
      return gateWidth(link) < gateWidth(narrowest) ? link : narrowest
    }, null)

// Reachability under a selection. Resolved by memoized recursion rather than a
// pass down the list: a gate may name a field that sits below its target (the
// editor only forbids cycles and waiting on a later-running group, not ordering),
// so position is no guide to what has been decided yet.
//
// Returns one row per admitted field with how it got there, and the fields the
// branch leaves out.
export const resolveBranch = ({ promptFields = [], links = [], selection = {} } = {}) => {
  const fieldsByKey = {}
  promptFields.forEach((field) => {
    const key = normalizeFieldKey(field.key)
    if (!isEmpty(key)) {
      fieldsByKey[key] = field
    }
  })

  // admitted: does this field run at all. conditional: it runs only if something
  // still unchosen goes its way, which has to travel downstream — a field whose
  // own gate is satisfied is still a maybe when its source was a maybe.
  const admitted = {}
  const conditional = {}
  const decidingByKey = {}
  const visiting = new Set()
  const logicKeys = getLogicKeys(promptFields)

  // The value a gate on `key` is evaluated against. A logic field nobody picked a
  // value for answers from the branch chosen above it, combined the way the
  // service does: `all` is No on any No and Yes only when every term holds, `any`
  // the reverse, and anything still open leaves it unchosen. A term on a field
  // this branch skips reads as no value, as the service reads a skipped input.
  // `isAdmitted` is resolve below, passed in since the two recurse into each
  // other.
  const derivedValues = {}
  const valueOf = (key, isAdmitted) => {
    const chosen = selection[key]
    if (!isNil(chosen) && chosen !== ANY_VALUE) {
      return chosen
    }
    if (!isLogicField(fieldsByKey[key])) {
      return ANY_VALUE
    }
    if (!isNil(derivedValues[key])) {
      return derivedValues[key]
    }
    // Stored data can hold a cycle the editor would refuse; read it as open.
    derivedValues[key] = ANY_VALUE
    const verdicts = getLinksForTarget(links, key)
      .filter((link) => !isNil(link.operator))
      .filter((link) => !isNil(fieldsByKey[normalizeFieldKey(link.source)]))
      .map((link) => {
        const sourceKey = normalizeFieldKey(link.source)
        return evaluateLink(link, isAdmitted(sourceKey) ? valueOf(sourceKey, isAdmitted) : NO_VALUE)
      })
    const isAll = fieldsByKey[key].value !== LOGIC_COMBINATOR.ANY
    const decisive = !isAll
    let answer = null
    if (verdicts.includes(decisive)) {
      answer = decisive
    } else if (!isEmpty(verdicts) && !verdicts.includes(null)) {
      answer = !decisive
    }
    derivedValues[key] = isNil(answer) ? ANY_VALUE : String(answer)
    return derivedValues[key]
  }

  const resolve = (key) => {
    if (!isNil(admitted[key])) {
      return admitted[key]
    }
    if (visiting.has(key)) {
      // The editor refuses to create a cycle; if stored data ever holds one,
      // admit the field rather than recurse forever.
      return true
    }
    visiting.add(key)

    const rowLinks = isLogicField(fieldsByKey[key])
      ? // Terms, not gates (see getLogicKeys): a logic field is always admitted.
        []
      : getLinksForTarget(links, key).filter(
          // A gate naming a field that isn't in the flow can't be evaluated, and
          // treating it as closed would blank the view over stale data.
          (link) => !isNil(fieldsByKey[normalizeFieldKey(link.source)])
        )
    let closed = false
    let unresolved = null
    let inherited = false

    rowLinks.forEach((link) => {
      const sourceKey = normalizeFieldKey(link.source)
      if (!resolve(sourceKey)) {
        closed = true
        return
      }
      if (conditional[sourceKey]) {
        inherited = true
      }
      const verdict = evaluateLink(link, valueOf(sourceKey, resolve))
      if (verdict === false) {
        closed = true
      } else if (verdict === null && isNil(unresolved)) {
        unresolved = link
      }
    })

    visiting.delete(key)
    admitted[key] = !closed
    conditional[key] = !closed && (!isNil(unresolved) || inherited)
    decidingByKey[key] = unresolved ?? narrowestLink(rowLinks)
    return admitted[key]
  }

  Object.keys(fieldsByKey).forEach(resolve)

  const rows = []
  const skipped = []
  promptFields.forEach((field) => {
    const key = normalizeFieldKey(field.key)
    if (isEmpty(key)) {
      return
    }
    if (!admitted[key]) {
      skipped.push(field)
      return
    }
    const decidingLink = decidingByKey[key]
    let provenance = PROVENANCE.always
    if (conditional[key]) {
      provenance = PROVENANCE.conditional
    } else if (!isNil(decidingLink)) {
      if (PRESENCE_OPERATORS.includes(decidingLink.operator)) {
        provenance = PROVENANCE.presence
      } else {
        provenance = gateWidth(decidingLink) === 1 ? PROVENANCE.only : PROVENANCE.shared
      }
    }

    rows.push({
      field,
      provenance,
      decidingLink,
      // How many of the deciding source's values admit this row — "shared · 13
      // Call Types". Zero for an ungated row and for a presence gate, neither of
      // which names values.
      reach: isNil(decidingLink) ? 0 : operandsOf(decidingLink).length,
      gatesCount: countTargets(links, field.key, logicKeys),
    })
  })

  return { rows, skipped }
}
